import { useMemo } from "react";

import { ProductType } from "@/constants";
import { useBalances } from "@/hooks/useBalances";
import useBaseCurrencyConverter from "@/hooks/useBaseCurrencyConverter";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useSettlementMethods } from "@/hooks/useSettlementMethods";
import { useCustomerAccount, useGlobalData } from "@/providers";
import { compareCurrencyRank } from "@/utils/sortProducts";

export type ActiveAccount = {
  type: ProductType;
  code: string; // currency code or product code
  balance: number;
  // Capabilities
  canPay: boolean;
  canTrade: boolean;
  canDeposit: boolean;
  // Trading restrictions
  tradingRestriction?: "TRADING_DESK_ONLY";
};

export type AvailableAccount = {
  type: ProductType;
  code: string;
  canTrade: boolean;
  canDeposit: boolean;
  tradingRestriction?: "TRADING_DESK_ONLY";
};

export type TradableProduct = {
  type: ProductType;
  code: string;
  name: string;
  tradingRestriction?: "TRADING_DESK_ONLY";
};

interface UseActiveAccountsResult {
  accounts: ActiveAccount[];
  availableAccounts: AvailableAccount[];
  tradableOnly: TradableProduct[];
  baseCurrency: string;
  isLoading: boolean;
  isInitialLoad: boolean;
  isError: boolean;
  refetch: () => void;
  isUninitialized: boolean;
}

export function useActiveAccounts(): UseActiveAccountsResult {
  const {
    balances,
    isLoading: isBalancesLoading,
    isError: isBalancesError,
    refetch,
    isUninitialized,
  } = useBalances();
  const { products, isLoadingProducts } = useGlobalData();
  const {
    tradable,
    payable,
    isLoading: isCapabilitiesLoading,
  } = useCapabilities();
  const {
    fiat: fiatSettlementMethods,
    blockchain: blockchainSettlementMethods,
    isLoading: isSettlementLoading,
  } = useSettlementMethods();
  const { convert } = useBaseCurrencyConverter();
  const { customerAccountBaseCurrency, hasDigitalAssets } =
    useCustomerAccount();
  const baseCurrency = customerAccountBaseCurrency;

  const isLoading =
    isBalancesLoading ||
    isLoadingProducts ||
    isCapabilitiesLoading ||
    isSettlementLoading;
  const isError = isBalancesError;

  // Build sets for quick lookup
  const payableSet = useMemo(
    () => new Set(payable.map((p) => p.id)),
    [payable]
  );
  const tradableMap = useMemo(() => {
    const map = new Map<
      string,
      { canTrade: boolean; restriction?: "TRADING_DESK_ONLY" }
    >();
    tradable.forEach((t) => {
      map.set(t.id, {
        canTrade:
          t.isTradable &&
          t.onlineQuotation?.state === "ON" &&
          !t.tradingRestriction,
        restriction: t.tradingRestriction,
      });
    });
    return map;
  }, [tradable]);

  // Build deposit availability sets
  const fiatDepositSet = useMemo(
    () => new Set(fiatSettlementMethods.map((m) => m.currency)),
    [fiatSettlementMethods]
  );
  const blockchainDepositSet = useMemo(
    () => new Set(blockchainSettlementMethods.map((m) => m.product)),
    [blockchainSettlementMethods]
  );

  const accounts: ActiveAccount[] = useMemo(() => {
    if (!products) return [];

    // 1. Start from balances (the existing source)
    const fromBalances = Object.entries(balances ?? {})
      .filter(([productCode]) => {
        if (!hasDigitalAssets) {
          const product = products.find((p) => p.code === productCode);
          if (product?.resourceType !== "currencies") return false;
        }
        return true;
      })
      .map(([productCode, balanceInfo]) => {
        const product = products.find((p) => p.code === productCode);
        const isCurrency = product?.resourceType === "currencies";
        const canPay = payableSet.has(productCode);
        const tradableInfo = tradableMap.get(productCode);
        const canTrade = tradableInfo?.canTrade ?? false;
        const canDeposit = isCurrency
          ? fiatDepositSet.has(productCode)
          : blockchainDepositSet.has(productCode);

        return {
          type: (isCurrency ? "currencies" : "digital-assets") as ProductType,
          code: productCode,
          balance: balanceInfo.balance,
          canPay,
          canTrade,
          canDeposit,
          tradingRestriction: tradableInfo?.restriction,
        };
      });

    // 2. Add zero-balance entries for every settlement method without a balance
    const balanceCodes = new Set(fromBalances.map((a) => a.code));
    const seenSettlementCodes = new Set<string>();
    const fromSettlementMethods: ActiveAccount[] = [];

    fiatSettlementMethods.forEach((method) => {
      const code = method.currency;
      if (balanceCodes.has(code) || seenSettlementCodes.has(code)) return;
      seenSettlementCodes.add(code);
      const canPay = payableSet.has(code);
      const tradableInfo = tradableMap.get(code);
      fromSettlementMethods.push({
        type: "currencies",
        code,
        balance: 0,
        canPay,
        canTrade: tradableInfo?.canTrade ?? false,
        canDeposit: true,
        tradingRestriction: tradableInfo?.restriction,
      });
    });

    if (hasDigitalAssets) {
      blockchainSettlementMethods.forEach((method) => {
        const code = method.product;
        if (balanceCodes.has(code) || seenSettlementCodes.has(code)) return;
        seenSettlementCodes.add(code);
        const tradableInfo = tradableMap.get(code);
        fromSettlementMethods.push({
          type: "digital-assets",
          code,
          balance: 0,
          canPay: payableSet.has(code),
          canTrade: tradableInfo?.canTrade ?? false,
          canDeposit: true,
          tradingRestriction: tradableInfo?.restriction,
        });
      });
    }

    const merged = [...fromBalances, ...fromSettlementMethods];

    // 3. Sort: base currency first, then by value-in-base (highest first), then
    //    equal-value accounts by currency rank (fiat majors, other fiat, blockchain)
    return merged.sort((a, b) => {
      if (a.code === baseCurrency) return -1;
      if (b.code === baseCurrency) return 1;
      const valueA = convert(a.balance, a.code);
      const valueB = convert(b.balance, b.code);
      if (valueA !== valueB) return valueB - valueA;
      return compareCurrencyRank(a, b);
    });
  }, [
    balances,
    products,
    baseCurrency,
    convert,
    payableSet,
    tradableMap,
    fiatDepositSet,
    blockchainDepositSet,
    fiatSettlementMethods,
    blockchainSettlementMethods,
    hasDigitalAssets,
  ]);

  // Set of product codes that are now active accounts (balance-backed or settlement-method-derived)
  const activeProductCodes = useMemo(
    () => new Set(accounts.map((a) => a.code)),
    [accounts]
  );

  // Available accounts: settlement methods without balance
  const availableAccounts: AvailableAccount[] = useMemo(() => {
    if (!products) return [];

    const availableSet = new Set<string>();
    const availableList: AvailableAccount[] = [];

    // Add fiat settlement methods not in active accounts
    fiatSettlementMethods.forEach((method) => {
      const code = method.currency;
      if (!activeProductCodes.has(code) && !availableSet.has(code)) {
        availableSet.add(code);
        const tradableInfo = tradableMap.get(code);
        availableList.push({
          type: "currencies",
          code,
          canTrade: tradableInfo?.canTrade ?? false,
          canDeposit: true,
          tradingRestriction: tradableInfo?.restriction,
        });
      }
    });

    // Add blockchain settlement methods not in active accounts
    blockchainSettlementMethods.forEach((method) => {
      const code = method.product;
      if (!activeProductCodes.has(code) && !availableSet.has(code)) {
        availableSet.add(code);
        const tradableInfo = tradableMap.get(code);
        availableList.push({
          type: "digital-assets",
          code,
          canTrade: tradableInfo?.canTrade ?? false,
          canDeposit: true,
          tradingRestriction: tradableInfo?.restriction,
        });
      }
    });

    // Sort by currency rank: fiat majors, then other fiat, then blockchain
    return availableList.sort(compareCurrencyRank);
  }, [
    products,
    fiatSettlementMethods,
    blockchainSettlementMethods,
    activeProductCodes,
    tradableMap,
  ]);

  // Tradable products not in active accounts (can still be in available accounts)
  const tradableOnly: TradableProduct[] = useMemo(() => {
    if (!products) return [];

    return (
      tradable
        .filter((t) => {
          // Must be tradable OR have a trading restriction (to show as disabled)
          if (!t.isTradable && !t.tradingRestriction) return false;
          // Not in active accounts
          if (activeProductCodes.has(t.id)) return false;
          // Not the base currency (shown as fallback when no active accounts)
          if (t.id === baseCurrency) return false;
          // When digital assets are disabled, hide crypto tradable products
          if (!hasDigitalAssets && t.resourceType !== "currencies")
            return false;
          return true;
        })
        .map((t) => {
          const product = products.find((p) => p.code === t.id);
          return {
            type: t.resourceType as ProductType,
            code: t.id,
            name: product?.name ?? t.id,
            tradingRestriction: t.tradingRestriction,
          } as TradableProduct;
        })
        // Currency rank already orders fiat before digital-assets, so this
        // replaces the previous type-then-name sort. Note the alphabetical
        // fallback is now on `code` rather than the product's display `name`.
        .sort(compareCurrencyRank)
    );
  }, [tradable, products, activeProductCodes, baseCurrency, hasDigitalAssets]);

  // Ready checks use *data availability* (not the aggregate isLoading flag).
  // GlobalData reports isLoadingProducts during its setTimeout deferral even
  // when products are already in the RTK cache — gating on that alone would
  // flash a skeleton on every /accounts visit.
  const isBalancesReady = !isUninitialized && !isBalancesLoading;
  const isProductsReady = products !== null;
  const isCapabilitiesReady = !isCapabilitiesLoading;
  const isSettlementReady = !isSettlementLoading;
  const isInitialLoad = !(
    isBalancesReady &&
    isProductsReady &&
    isCapabilitiesReady &&
    isSettlementReady
  );

  // Memoize the return so downstream consumers (LedgerEntryHeader, accounts
  // layout/page, AccountsList, …) don't see a fresh object every render and
  // bust their own memo deps. See .claude/rules/web/render-stability.md Rule #2.
  return useMemo(
    () => ({
      accounts,
      availableAccounts,
      tradableOnly,
      baseCurrency,
      isLoading,
      isInitialLoad,
      isError,
      refetch,
      isUninitialized,
    }),
    [
      accounts,
      availableAccounts,
      tradableOnly,
      baseCurrency,
      isLoading,
      isInitialLoad,
      isError,
      refetch,
      isUninitialized,
    ]
  );
}

export default useActiveAccounts;
